// ระบบจัดการรูปภาพหลัก (Profile Image)
// จัดการการเลือกและแสดงรูปภาพหลักของน้อง

/**
 * ดึงรูปภาพหลักของน้อง
 * @param {object} girlData - ข้อมูลน้อง
 * @returns {string|null} - URL ของรูปภาพหลัก หรือ null
 */
export const getProfileImage = (girlData) => {
  if (!girlData) return null;
  
  // 1. ตรวจสอบว่ามีการระบุรูปหลักหรือไม่
  if (girlData.profileImage && girlData.profileImage.url) {
    return girlData.profileImage.url;
  }
  
  // 2. ตรวจสอบรูปภาพที่อนุมัติแล้ว
  if (girlData.images && girlData.images.length > 0) {
    // หารูปภาพแรกที่อนุมัติแล้ว
    const approvedImage = girlData.images.find(img => img.status === 'approved' || !img.status);
    if (approvedImage) {
      return approvedImage.url;
    }
  }
  
  // 3. Fallback: ใช้รูปภาพแรกจาก pendingImages
  if (girlData.pendingImages && girlData.pendingImages.length > 0) {
    return girlData.pendingImages[0].url;
  }
  
  return null;
};

/**
 * ดึงรูปภาพหลักพร้อม fallback
 * @param {object} girlData - ข้อมูลน้อง
 * @param {string} fallbackImage - รูปภาพ fallback
 * @returns {string} - URL ของรูปภาพหลักหรือ fallback
 */
export const getProfileImageWithFallback = (girlData, fallbackImage = null) => {
  const profileImage = getProfileImage(girlData);
  if (profileImage) return profileImage;
  
  // สร้าง placeholder image ถ้าไม่มีรูป
  if (!fallbackImage && girlData) {
    const { createProfilePlaceholder } = require('./placeholderImageUtils');
    return createProfilePlaceholder(girlData.nickname || 'น้องใหม่', girlData.type || 'normal');
  }
  
  return fallbackImage || '/default-avatar.png';
};

/**
 * ตรวจสอบว่าน้องมีรูปภาพหลักหรือไม่
 * @param {object} girlData - ข้อมูลน้อง
 * @returns {boolean}
 */
export const hasProfileImage = (girlData) => {
  return getProfileImage(girlData) !== null;
};

/**
 * ดึงรายการรูปภาพที่สามารถใช้เป็นรูปหลักได้
 * @param {object} girlData - ข้อมูลน้อง
 * @returns {Array} - รายการรูปภาพที่อนุมัติแล้ว
 */
export const getAvailableProfileImages = (girlData) => {
  if (!girlData) return [];
  
  const availableImages = [];
  
  // เพิ่มรูปภาพที่อนุมัติแล้ว
  if (girlData.images && girlData.images.length > 0) {
    girlData.images.forEach((img, index) => {
      availableImages.push({
        ...img,
        status: 'approved',
        displayName: `รูปที่ ${index + 1}`,
        isCurrentProfile: girlData.profileImage && girlData.profileImage.url === img.url
      });
    });
  }
  
  return availableImages;
};

/**
 * สร้างข้อมูลรูปภาพหลัก
 * @param {object} image - ข้อมูลรูปภาพ
 * @returns {object} - ข้อมูลรูปภาพหลัก
 */
export const createProfileImageData = (image) => {
  return {
    url: image.url,
    fileName: image.fileName || image.originalName,
    uploadedAt: image.uploadedAt || new Date().toISOString(),
    selectedAt: new Date().toISOString(),
    thumbnail: image.thumbnail || image.url
  };
};

/**
 * ตรวจสอบว่ารูปภาพนี้เป็นรูปหลักหรือไม่
 * @param {object} girlData - ข้อมูลน้อง
 * @param {string} imageUrl - URL ของรูปภาพ
 * @returns {boolean}
 */
export const isProfileImage = (girlData, imageUrl) => {
  if (!girlData || !girlData.profileImage) return false;
  return girlData.profileImage.url === imageUrl;
};

/**
 * ดึงข้อมูลรูปภาพหลักพร้อมข้อมูลเพิ่มเติม
 * @param {object} girlData - ข้อมูลน้อง
 * @returns {object|null} - ข้อมูลรูปภาพหลักพร้อมข้อมูลเพิ่มเติม
 */
export const getProfileImageInfo = (girlData) => {
  if (!girlData) return null;
  
  const profileImage = getProfileImage(girlData);
  if (!profileImage) return null;
  
  return {
    url: profileImage,
    isCustomSelected: !!(girlData.profileImage && girlData.profileImage.url),
    selectedAt: girlData.profileImage?.selectedAt,
    fileName: girlData.profileImage?.fileName,
    thumbnail: girlData.profileImage?.thumbnail || profileImage
  };
};
